import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DownOutlined } from '@ant-design/icons';
import { Tree } from 'antd';
import {
  NoDataContainer,
  SearchWrapper,
  TitleWrapper,
  TreeWrapper,
} from './TreeComponentStyled';
import {
  setExpandedKeys,
  setSelectedKeys,
  setTabClick,
  setUpdateMeta,
  postHighlightedMatter,
  postLastPageViewed,
  fetchMatterDetail,
  fetchMatterDetailList,
} from '../../slice/MatterDetailsSlice';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import Search from '../Search/CommonSearch';
import { onSelect, onExpand, handleTitleClick } from './TreeComponentUtils';
import FolderContextMenu from './FolderContextMenu';
import DocumentContextMenu from './DocumentContextMenu';
import CombineFilesModal from './CombineFilesModal';
import TreeNodeFullName from './TreeNodeFullName';
import ClipboardPasteBar from '../TabView/ClipboardPasteBar';
import { copyDocumentsApi } from '../../service/matter';
import { selectClipboardItems } from '../../slice/DocumentClipboardSlice';
import { addNotification } from '../../slice/SnackbarSlice';
import {
  extractCopyApiError,
  findTreePasteTarget,
  formatPasteNote,
  isTreeBackgroundContextMenu,
  pasteMenuLabel,
  retargetTreeRowContextMenu,
} from '../../utils/documentClipboard';
import {
  handleContainerDragOver,
  handleContainerDrop,
  startDocumentDrag,
  selectCurrentMatterId,
  getSelectedDocumentNodes,
  getFolderIdFromNode,
} from './documentMoveUtils';

// Folder-name extraction for the parent-folder-name fallback below: prefer the
// BE folder name on `record.name`, fall back to a stringy node title. Returns ""
// for anything we can't resolve so DocumentContextMenu's fallback chain stays
// well-defined.
const extractParentFolderName = (node) => {
  if (!node || typeof node !== 'object') return '';
  if (node.record && typeof node.record.name === 'string' && node.record.name) {
    return node.record.name;
  }
  if (typeof node.title === 'string') return node.title;
  return '';
};

// Recursively wrap nested folder + document titles in their respective
// context-menu wrappers so right-click works on every row in the tree, not
// just the top-level ones. The top-level wrapping is applied separately
// below at render time so we can preserve the existing Box
// click-to-expand affordance and place the context menu as the OUTERMOST
// wrapper (the Dropdown's contextMenu listener must sit on the outermost
// element to catch right-clicks anywhere in the row).
//
// `parentFolderName` threads the immediate-parent folder's display name into
// each document leaf so DocumentContextMenu can infer `wrongCategory` from
// the sidebar layout when the BE serializer omits `doc_category`. Folder
// children don't inherit a parent name (folders carry their own identity);
// only document leaves consume it.
// Every row's title is wrapped in a span carrying data-tree-node-key so the
// custom drag-and-drop layer (documentMoveUtils) can resolve which row the
// pointer is actually over via event.target.closest. Document spans are
// additionally the DRAG SOURCE (native HTML5 draggable + dragstart payload)
// — we bypass antd Tree's built-in DnD entirely because rc-tree only
// attaches drop handlers to rows that are themselves draggable, which makes
// folder rows drop dead-zones when only documents drag (the live bug this
// replaced). `dropTargetFolderKey` paints the live destination folder row.
//
// The same span is the host for the hover-to-reveal full name
// (TreeNodeFullName), so EVERY row — top-level or nested, folder or
// document, plain or search-filtered — gets the identical, non-interfering
// popup. The plain-text name comes from the node title when it is still a
// string, from `fullName` when search mode has replaced the title with a
// highlighted element, and from the BE record as a last resort.
const resolveNodeFullName = (node) => {
  if (!node || typeof node !== 'object') return undefined;
  if (typeof node.title === 'string') return node.title;
  if (typeof node.fullName === 'string') return node.fullName;
  if (node.record && typeof node.record.name === 'string') return node.record.name;
  return undefined;
};

const wrapTitleWithNodeKey = (node, title, dropTargetFolderKey, getSelectedDocNodes) => {
  const isDropTarget =
    node?.type === 'folder' &&
    node?.key != null &&
    node.key === dropTargetFolderKey;
  const isDocument = node?.type === 'document';
  return (
    <TreeNodeFullName
      name={resolveNodeFullName(node)}
      data-tree-node-key={node?.key}
      className={isDropTarget ? 'dms-tree-drop-target' : undefined}
      style={{ display: 'block', width: '100%' }}
      draggable={isDocument || undefined}
      onDragStart={
        isDocument
          ? (event) => {
              const selectedDocumentNodes = getSelectedDocNodes
                ? getSelectedDocNodes()
                : undefined;
              if (!startDocumentDrag({ event, node, selectedDocumentNodes })) {
                event.preventDefault();
              }
              event.stopPropagation();
            }
          : undefined
      }
    >
      {title}
    </TreeNodeFullName>
  );
};

const wrapNestedNodes = (
  nodes,
  parentFolderName = '',
  dropTargetFolderKey = null,
  getSelectedDocNodes = undefined,
  onCombineRequest = undefined
) => {
  if (!Array.isArray(nodes)) return nodes;
  return nodes.map((node) => {
    if (!node || typeof node !== 'object') return node;
    const childParentFolderName =
      node.type === 'folder' ? extractParentFolderName(node) : '';
    const wrappedChildren = Array.isArray(node.children)
      ? wrapNestedNodes(node.children, childParentFolderName, dropTargetFolderKey, getSelectedDocNodes, onCombineRequest)
      : node.children;
    if (node.type === 'folder') {
      return {
        ...node,
        title: (
          <FolderContextMenu folderNode={node}>
            {wrapTitleWithNodeKey(node, node.title, dropTargetFolderKey, getSelectedDocNodes)}
          </FolderContextMenu>
        ),
        children: wrappedChildren,
      };
    }
    if (node.type === 'document') {
      return {
        ...node,
        title: (
          <DocumentContextMenu
            documentNode={node}
            parentFolderName={parentFolderName}
            onCombineRequest={onCombineRequest}
          >
            {wrapTitleWithNodeKey(node, node.title, dropTargetFolderKey, getSelectedDocNodes)}
          </DocumentContextMenu>
        ),
        children: wrappedChildren,
      };
    }
    return wrappedChildren === node.children
      ? node
      : { ...node, children: wrappedChildren };
  });
};

const TreeComponent = ({ treeData, setUniqueId, hasNavigatedRef, customOnSelect, checkTabLimit, openTab, setActiveTab }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedKeys, expandedKeys } = useSelector((state) => state.matterDetails);
  const matterId = useSelector(selectCurrentMatterId);
  const clipboardItems = useSelector(selectClipboardItems);
  const [pastingClipboard, setPastingClipboard] = useState(false);
const location = useLocation();
  const [searchValue, setSearchValue] = useState('');
  const [filteredTreeData, setFilteredTreeData] = useState(treeData);
  const [searchExpandKeys, setSearchExpandKeys] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [savedExpandedKeys, setSavedExpandedKeys] = useState([]);
  // Folder row currently highlighted as the live drag-and-drop destination.
  const [dropTargetFolderKey, setDropTargetFolderKey] = useState(null);

  // "Combine files" (multi-select context-menu action) is owned HERE rather
  // than by the per-row DocumentContextMenu that requests it: TreeComponent
  // is mounted once for the whole sidebar, so the naming/destination modal
  // and its progress toast survive a tree refresh that lands mid-job.
  const [combineModalOpen, setCombineModalOpen] = useState(false);
  const [combineDocuments, setCombineDocuments] = useState([]);
  const [treePasteMenu, setTreePasteMenu] = useState(null);
  const rawTree = useSelector((state) => state.matterDetails?.matterDetailsTree);
  const listCurrentPage = useSelector((state) => state.matterDetails?.currentPage);
  const listSort = useSelector((state) => state.matterDetails?.sort);
  const listSortBy = useSelector((state) => state.matterDetails?.sortBy);
  const listState = { currentPage: listCurrentPage, sort: listSort, sortBy: listSortBy };
  const currentFolderId = useSelector((state) => state.websocket?.currentFolderId);
  const openCombineModal = useCallback((documents) => {
    setCombineDocuments(Array.isArray(documents) ? documents : []);
    setCombineModalOpen(true);
  }, []);
  const closeCombineModal = useCallback(() => setCombineModalOpen(false), []);

  const handlePasteIntoMatter = useCallback(() => {
    if (pastingClipboard || !clipboardItems.length || !matterId) return;
    const target = findTreePasteTarget(filteredTreeData, selectedKeys);
    const targetFolderId = target ? getFolderIdFromNode(target) : null;
    const destinationName = target
      ? target.record?.name ||
        (typeof target.title === "string" ? target.title : "this folder")
      : "this matter";
    setPastingClipboard(true);
    copyDocumentsApi({
      documentIds: clipboardItems.map((item) => item.id),
      targetMatterId: matterId,
      targetFolderId,
    })
      .then((response) => {
        const payload = response?.data || {};
        dispatch(
          addNotification({
            id: `${Date.now()}-tree-paste`,
            newMsg: formatPasteNote({
              copied: payload.copied || 0,
              failed: payload.failed || 0,
              results: payload.results || [],
              destinationName,
            }),
            success: (payload.failed || 0) === 0,
          })
        );
        dispatch(fetchMatterDetail(matterId));
        dispatch(
          fetchMatterDetailList({
            id: matterId,
            page: listState.currentPage || 1,
            sort_direction: listState.sort,
            sort_by: listState.sortBy,
          })
        );
      })
      .catch((err) => {
        dispatch(
          addNotification({
            id: `${Date.now()}-tree-paste-error`,
            newMsg: extractCopyApiError(err, "Paste failed"),
            success: false,
          })
        );
      })
      .finally(() => setPastingClipboard(false));
  }, [
    clipboardItems,
    dispatch,
    filteredTreeData,
    listState,
    matterId,
    pastingClipboard,
    selectedKeys,
  ]);

  const handleTreeRightClick = useCallback(({ event }) => {
    retargetTreeRowContextMenu(event);
  }, []);

  const handleTreePaneContextMenu = useCallback(
    (event) => {
      if (!clipboardItems.length) return;
      if (!isTreeBackgroundContextMenu(event)) return;
      event.preventDefault();
      setTreePasteMenu({ x: event.clientX, y: event.clientY });
    },
    [clipboardItems.length]
  );

  useEffect(() => {
    if (!treePasteMenu) return undefined;
    const close = (event) => {
      if (event.target?.closest?.('[data-testid="tree-background-paste-menu"]')) {
        return;
      }
      setTreePasteMenu(null);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setTreePasteMenu(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [treePasteMenu]);

  // Live selection snapshot for drag sources: dragstart closures are built
  // at render time, so read through a ref to always see the current
  // multi-selection without re-wrapping every row on selection change.
  const dragSelectionRef = useRef({ selectedKeys: [], treeData: [] });
  dragSelectionRef.current = { selectedKeys, treeData: filteredTreeData };
  const getSelectedDocNodes = useCallback(
    () =>
      getSelectedDocumentNodes(
        dragSelectionRef.current.treeData,
        dragSelectionRef.current.selectedKeys
      ),
    []
  );

  useEffect(() => {
    const isNowSearching = !!searchValue;

    const highlightMatch = (title) => {
    const index = title.toLowerCase().indexOf(searchValue.toLowerCase());
    if (index === -1) return <span>{title}</span>;

    const before = title.slice(0, index);
    const match = title.slice(index, index + searchValue.length);
    const after = title.slice(index + searchValue.length);

    return (
      <>
        {before}
        <span style={{ background: '#FFCE2E4D' }}>{match}</span>
        {after}
      </>
    );
  };

    if (isNowSearching && !isSearching) {
      setSavedExpandedKeys(expandedKeys);
      setIsSearching(true);
    }

    if (!isNowSearching && isSearching) {
      dispatch(setExpandedKeys(savedExpandedKeys));
      setIsSearching(false);
    }

    if (!searchValue) {
      setFilteredTreeData(treeData);
      return;
    }

    const expandKeys = [];

    const filterTree = (nodes) => {
      return nodes
        .map((node) => {
          const match = node?.title?.toLowerCase().includes(searchValue?.toLowerCase());

          if (node?.children) {
            const children = filterTree(node?.children);
            if (children.length > 0 || match) {
              if (children.length > 0) expandKeys.push(node.key);
              // `fullName` keeps the plain string around for the row-level
              // hover popup once the title becomes a highlighted element.
              return {
                ...node,
                fullName: node?.title,
                title: <TitleWrapper>{highlightMatch(node?.title)}</TitleWrapper>,
                children,
              };
            }
          }

          if (match) {
            return {
              ...node,
              fullName: node?.title,
              title: <TitleWrapper>{highlightMatch(node?.title)}</TitleWrapper>,
            };
          }

          return null;
        })
        .filter(Boolean);
    };

    const filtered = filterTree(treeData);
    setFilteredTreeData(filtered);
    setSearchExpandKeys(expandKeys);
  }, [searchValue, treeData , expandedKeys, dispatch, savedExpandedKeys, isSearching]);


  useEffect(() => {
    const checkHighlighted = (nodes) => {
      if (!Array.isArray(nodes)) return;

      for (const node of nodes) {
        if (node.is_highlighted === true && selectedKeys.length === 0) {
          return; 
        }

        if (node.children && node.children.length > 0) {
          checkHighlighted(node.children);
          if (hasNavigatedRef.current) return;
        }
      }
    };

    if (treeData && treeData.length > 0) {
      checkHighlighted(treeData);
    }
  }, [treeData, selectedKeys]);

  const renderSwitcherIcon = () => <DownOutlined style={{ color: '#5A6C87' }} />;

  const pasteTarget = findTreePasteTarget(filteredTreeData, selectedKeys);
  const pasteDestinationName = pasteTarget
    ? pasteTarget.record?.name ||
      (typeof pasteTarget.title === "string" ? pasteTarget.title : "this folder")
    : "this matter";

  return (
    <>
    <TreeWrapper>
      <SearchWrapper>
        <Search
          searchPlaceholder={"Search"}
          isLoading={false}
          handleSearch={() => {}}
          handleSearchOnChange={setSearchValue}
          inputValue={searchValue}
          searchbarwidth="100%"
        />
        {clipboardItems.length > 0 && (
          <ClipboardPasteBar
            compact
            count={clipboardItems.length}
            destinationName={pasteDestinationName}
            onPaste={handlePasteIntoMatter}
            pasting={pastingClipboard}
          />
        )}
      </SearchWrapper>

      {/* Custom drag-and-drop layer (NOT antd Tree's built-in DnD — rc-tree
          only attaches drop handlers to rows that are themselves draggable,
          so with document-only dragging, folder rows never receive drops).
          Document title spans are native draggable sources (see
          wrapTitleWithNodeKey); this container delegates dragover/drop for
          every row, gated on our custom drag MIME so desktop-file uploads
          and text drags pass through untouched. dragEnd on the container
          catches releases anywhere (drag events bubble from the source). */}
      <div
        data-testid="tree-pane"
        onContextMenu={handleTreePaneContextMenu}
        style={{ flex: 1, minHeight: 48 }}
      >
      {filteredTreeData.length>0?<div
        data-testid="tree-dnd-container"
        onDragOver={(event) =>
          setDropTargetFolderKey(
            handleContainerDragOver({ event, treeData: filteredTreeData })
          )
        }
        onDrop={(event) => {
          setDropTargetFolderKey(null);
          handleContainerDrop({
            event,
            dispatch,
            matterId,
            treeData: filteredTreeData,
          });
        }}
        onDragEnd={() => setDropTargetFolderKey(null)}
      ><Tree
        multiple
        switcherIcon={renderSwitcherIcon}
        onRightClick={handleTreeRightClick}
        showIcon
        onSelect={(selectedKeys, info) => {
          if (customOnSelect) {
            customOnSelect(selectedKeys, info);
          } else {
            onSelect({selectedKeys, info, dispatch, postHighlightedMatter, postLastPageViewed, fetchMatterDetail, location, setSelectedKeys, setTabClick, setUpdateMeta, setUniqueId, navigate, checkTabLimit, openTab, setActiveTab});
          }
        }}
        onExpand={(keys) => onExpand(keys, isSearching, setSearchExpandKeys, dispatch, setExpandedKeys)}
        selectedKeys={selectedKeys}
        expandedKeys={isSearching ? searchExpandKeys : expandedKeys}
        treeData={filteredTreeData.map((item) => {
          const interactiveTitle = (
            <Box
              tabIndex={0}
              onClick={() => handleTitleClick(item.key, isSearching, searchExpandKeys, expandedKeys, setSearchExpandKeys, dispatch, setExpandedKeys)}
              onKeyDown={(e) =>
                (e.key === 'Enter' || e.key === ' ') && handleTitleClick(item.key, isSearching, searchExpandKeys, expandedKeys, setSearchExpandKeys, dispatch, setExpandedKeys)
              }
            >
              {/* Full-name hover lives on the row span (wrapTitleWithNodeKey),
                  not here, so top-level and nested rows behave identically. */}
              <Box  sx={{
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}>
                {item.title}
              </Box>
            </Box>
          );
          // FolderContextMenu / DocumentContextMenu sit OUTERMOST on tree
          // rows so their Dropdown contextMenu listeners catch right-clicks
          // anywhere in the row. Folders get folder-scoped actions
          // (rename/delete/new subfolder); documents get the
          // categorization gold-standard library actions matching the
          // matter document table's row-action menu.
          let finalTitle = interactiveTitle;
          if (item?.type === 'folder') {
            finalTitle = (
              <FolderContextMenu folderNode={item}>
                {wrapTitleWithNodeKey(item, interactiveTitle, dropTargetFolderKey, getSelectedDocNodes)}
              </FolderContextMenu>
            );
          } else if (item?.type === 'document') {
            // Top-level documents sit directly under the matter root rather
            // than under a category folder, so no canonical category can be
            // inferred from layout. Leave parentFolderName empty and the
            // menu correctly stays disabled when the BE didn't supply a
            // category.
            finalTitle = (
              <DocumentContextMenu
                documentNode={item}
                parentFolderName=""
                onCombineRequest={openCombineModal}
              >
                {wrapTitleWithNodeKey(item, interactiveTitle, dropTargetFolderKey, getSelectedDocNodes)}
              </DocumentContextMenu>
            );
          }
          const childParentFolderName =
            item?.type === 'folder' ? extractParentFolderName(item) : '';
          return {
            ...item,
            title: finalTitle,
            children: Array.isArray(item.children)
              ? wrapNestedNodes(item.children, childParentFolderName, dropTargetFolderKey, getSelectedDocNodes, openCombineModal)
              : item.children,
          };
        })}
      /></div>:<NoDataContainer>No search results</NoDataContainer>}
      </div>
      {treePasteMenu && clipboardItems.length > 0 && (
        <div
          data-testid="tree-background-paste-menu"
          role="menu"
          style={{
            position: "fixed",
            left: treePasteMenu.x,
            top: treePasteMenu.y,
            zIndex: 1100,
            minWidth: 160,
            padding: "4px 0",
            background: "#fff",
            border: "1px solid #d9d9d9",
            borderRadius: 8,
            boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
          }}
        >
          <button
            type="button"
            role="menuitem"
            data-testid="tree-background-paste"
            onClick={() => {
              setTreePasteMenu(null);
              handlePasteIntoMatter();
            }}
            style={{
              display: "block",
              width: "100%",
              border: "none",
              background: "transparent",
              textAlign: "left",
              padding: "5px 12px",
              cursor: "pointer",
              fontFamily: "Manrope, sans-serif",
              fontSize: 13,
              color: "#1f1f1f",
            }}
          >
            {pasteMenuLabel(clipboardItems.length)}
          </button>
        </div>
      )}
    </TreeWrapper>
    <CombineFilesModal
      open={combineModalOpen}
      onClose={closeCombineModal}
      documents={combineDocuments}
      matterId={matterId}
      rawTree={rawTree}
      currentFolderId={currentFolderId}
      listRefreshArgs={listState}
      navigate={navigate}
    />
    </>
  );
};

TreeComponent.propTypes = {
  treeData: PropTypes.array.isRequired,
  setUniqueId: PropTypes.func.isRequired,
  hasNavigatedRef: PropTypes.shape({
    current: PropTypes.bool
  }).isRequired,
  customOnSelect: PropTypes.func,
  checkTabLimit: PropTypes.func,
  openTab: PropTypes.func,
  setActiveTab: PropTypes.func,
};

export default TreeComponent;